import { Action, Reducer } from 'redux';
import * as H from 'history';
import { ApplicationState, AppThunkAction, AppThunkActionAsync } from './../../';
import { ToxnotServiceClient } from '../../ToxnotServiceClient';
import * as Models from '../../Models/Customers/Customer';
import { CreateReportRequest, ReportTypeWithContext, UpdateCustomerRequest } from '../../Models/Api/CustomerApiRequestsAndResponses';
import SaveDataManager from '../../../utilities/SaveDataManager';
import { Filter, FilterId } from '../../Models/Filter';
import { ResultType } from '../../Models/Api/ApiResponse';
import { AppServices } from '../../../AppServices';
import { IStringLocalizer } from '../../../utilities/IStringLocalizer';
import { SharedMaterialIdToCoreDataTable, SharedMaterialsFullCompany } from '../../Models/SharedMaterials/DataModel';
import { BaseOfflineProcessData, BulkReportingResults, OfflineProcessingStageType, OfflineProcessState } from '../../Models/OfflineProcesses/OfflineProcessModels';
import { OfflineProcessPollingManager } from '../../Managers/OfflineProcessPollingManager';
import { BulkReportContext } from '../BulkReport/BulkReportStateController';
import { ToxnotReactComponentPaths } from '../../../components/core/ToxnotReactComponentPaths';
import { ReportTypesManager } from '../../Managers/ReportTypesManager';
import { MyCustomersFilterCheckContext, MyCustomersFilteringManager } from './CustomerFiltering';
import { InventoryNameWithPossibleId } from '../../Models/InventoryNameWithPossibleId';
import { DynamicRegulation } from '../../Models/Compliance/DynamicRegulation';

// ===============================================================================

export enum PickReportsModalResponseState {
    Default,
    CreatingReport,
    ReportCreated
}

type RequestStatusesType = { [requestId: string]: OfflineProcessState }
type CustomerIdToCustomerType = { [customerId: string]: Models.Customer }
type SelectedCustomerIdsType = { [customerId: string]: string };

export interface MyCustomersState {
    filteredCustomers: Models.Customer[];
    activeCustomerFilters: Filter[];
    myCustomers: Models.Customer[];
    selectedCustomerIds: SelectedCustomerIdsType;
    customerIdsToRequests: Models.CustomerIdToRequestsType | null;
    customerIdsToPurchases: Models.CustomerIdToPurchasesType;
    isLoading: boolean;
    customerIdToCustomerTable: CustomerIdToCustomerType;
    currentlySelectedCustomerId: string | null;
    sharedMaterialIdsToCoreDataTable: SharedMaterialIdToCoreDataTable;
    sharedMaterialCompanies: SharedMaterialsFullCompany[];
    respondingRequestIdForModals: string | null;
    requestsResponding: RequestStatusesType;
    requestForCreateReportRequest: CreateReportRequest | null;
    pickReportsModalResponseState: PickReportsModalResponseState;
}

const defaultOfflineProcessState: OfflineProcessState = {
    processingStage: OfflineProcessingStageType.Queued,
    progressTotalNumItems: 0,
    currentProgressItemIndex: 0
}

const initialState: MyCustomersState = {
    isLoading: true,
    myCustomers: [],
    filteredCustomers: [],
    activeCustomerFilters: [],
    customerIdsToRequests: null,
    customerIdsToPurchases: {},
    customerIdToCustomerTable: {},
    selectedCustomerIds: {},
    currentlySelectedCustomerId: null,
    sharedMaterialIdsToCoreDataTable: {},
    sharedMaterialCompanies: [],
    respondingRequestIdForModals: null,
    requestsResponding: {},
    requestForCreateReportRequest: null,
    pickReportsModalResponseState: PickReportsModalResponseState.Default
}

// ===============================================================================

//
// Action definitions
//

const UpdateCustomersState = "UPDATE_CUSTOMERS_STATE";

interface UpdateCustomersStateAction {
    type: typeof UpdateCustomersState;
    value: Partial<MyCustomersState>;
}

const toxnotServiceClient = new ToxnotServiceClient();
let offlineProcessManager: OfflineProcessPollingManager<BulkReportContext, BulkReportingResults> | null = null;

// ===============================================================================

//
// Helper Functions
//

// ===============================================================================

export function getCustomerContactNamesAsString(contacts: Models.CustomerContact[]) {
    return contacts.map(c => c.name).join(", ").coalesceToMaxSizeAndIndicateTruncationWithEllipsis(25);
}

// ===============================================================================

export function getCustomerContactEmailsAsString(contacts: Models.CustomerContact[]) {
    return contacts.map(c => c.email).join(", ").coalesceToMaxSizeAndIndicateTruncationWithEllipsis(25);
}

// ===============================================================================

export function getInventoriesToReportOnAsString(inventoriesToReport: InventoryNameWithPossibleId[], localizer: IStringLocalizer) {
    return inventoriesToReport
        .map(i => i.name)
        .truncateAndJoinListOfStringsWithCountDescription(localizer, 3, ", ");
}

// ===============================================================================

export function getReportTypesAsDisplayableList(request: Models.CustomerRequest, localizer: IStringLocalizer, dynRegs: DynamicRegulation[]) {
    let reportTypesToUse: string[] = [];

    for (let r of request.requestData.reportTypesAndContexts) {
        const context = r.contextJson === null ? null : JSON.parse(r.contextJson) as Models.ReportContext;

        let reportString = "";

        if (context === null) {
            reportString = ReportTypesManager.getFriendlyNameForReportTypeOrRegulation(r.reportType, localizer, dynRegs);
        }
        else {
            const sortedRegulations = ReportTypesManager.getSortedListOfReportTypesOrRegulations(context.regulations, localizer, dynRegs);
            reportString = ReportTypesManager.getFriendlyNameForReportTypeOrRegulation(r.reportType, localizer, dynRegs) + " [" + sortedRegulations.map(reg => ReportTypesManager.getFriendlyNameForReportTypeOrRegulation(reg, localizer, dynRegs)).join(", ") + "]";
        }

        reportTypesToUse.push(reportString)
    }

    return reportTypesToUse;
}

// ===============================================================================

export function getReportTypesAsDisplayableString(request: Models.CustomerRequest, localizer: IStringLocalizer, dynRegs: DynamicRegulation[], truncate: boolean = true) {
    return getReportTypesAsDisplayableList(request, localizer, dynRegs).truncateAndJoinListOfStringsWithCountDescription(localizer, truncate ? 3 : 99999, ", ");
}

// ===============================================================================

function verifyAndGetMyCustomerStateFromAppState(appState: ApplicationState) {
    if (!appState) {
        throw new Error("Application State was undefined.");
    }

    if (!appState.myCustomersState) {
        throw new Error("My Customers State was undefined.");
    }

    return appState.myCustomersState;
}

// ===============================================================================

function verifyAndGetMyCustomerState(getState: () => ApplicationState) {
    return verifyAndGetMyCustomerStateFromAppState(getState());
}

// ===============================================================================

export function getRequestFromStateOrNull(customersState: MyCustomersState, requestId: string | null) {
    if (customersState.currentlySelectedCustomerId === null) {
        return null;
    }

    if (requestId === null) {
        return null;
    }

    if (customersState.customerIdsToRequests === null) {
        return null;
    }

    const request = customersState.customerIdsToRequests[customersState.currentlySelectedCustomerId].find(r => r.requestId === requestId);

    if (!request) {
        return null;
    }

    return request;
}

// ===============================================================================

export function getRequestFromState(customersState: MyCustomersState, requestId: string | null) {
    const request = getRequestFromStateOrNull(customersState, requestId);

    if (request === null) {
        throw new Error("Request could not be found");
    }

    return request;
}

// ===============================================================================

export function getCurrentlySelectedCustomer(customersState: MyCustomersState) {
    if (customersState.currentlySelectedCustomerId === null) {
        throw new Error("Could not fetch currently selected customer. Currently selected customer ID was null");
    }

    return customersState.customerIdToCustomerTable[customersState.currentlySelectedCustomerId];
}

// ===============================================================================

function createCustomerIdToCustomerTable(customers: Models.Customer[]) {
    const table: CustomerIdToCustomerType = {}

    for (let c of customers) {
        table[c.customerId] = c;
    }

    return table;
}

// ===============================================================================

export function getCompanyCustomReportsFromState(myCustomersState: MyCustomersState) {
    if (myCustomersState.sharedMaterialCompanies.length === 0) {
        return null;
    }

    return myCustomersState.sharedMaterialCompanies.selectMany(c => c.reports).distinct();
}

// ===============================================================================

async function loadAllData(appState: ApplicationState, dispatch: (action: UpdateCustomersStateAction) => void) {
    dispatch({ type: UpdateCustomersState, value: { isLoading: true } });

    const apiResult = await toxnotServiceClient.getCustomersBundle();

    if (!apiResult || !apiResult.data) {
        appState.appServices?.displayApiErrorMessage();
        return;
    }

    const table = createCustomerIdToCustomerTable(apiResult.data.customers);

    dispatch({
        type: UpdateCustomersState,
        value: {
            myCustomers: apiResult.data.customers,
            customerIdsToRequests: apiResult.data.customerIdToRequests,
            customerIdsToPurchases: apiResult.data.customerIdToPurchases,
            filteredCustomers: apiResult.data.customers,
            isLoading: false,
            customerIdToCustomerTable: table,
            sharedMaterialCompanies: apiResult.data.sharedMaterialCompanies
        }
    });
}

// ===============================================================================

function applyCustomerFilters(customers: Models.Customer[], customerIdToRequests: Models.CustomerIdToRequestsType | null, newActiveFilters: Filter[]) {
    const filteringManager = new MyCustomersFilteringManager();

    const context: MyCustomersFilterCheckContext = {
        customerIdToRequests: customerIdToRequests
    };

    return filteringManager.getFilteredArray(customers, context, newActiveFilters);
}

// ===============================================================================

function removeFilter(filterToRemove: Filter, allFilters: Filter[]) {
    return allFilters.filter(f => f.filterId !== filterToRemove.filterId || f.value !== filterToRemove.value);
}

// ===============================================================================

function saveCustomerToService(appservices: AppServices, updatedCustomer: UpdateCustomerRequest) {
    const saveDataManager = new SaveDataManager(() => toxnotServiceClient.updateCustomer(updatedCustomer).then(r => r.success), appservices);
    saveDataManager.initialize();
    saveDataManager.enable();
    saveDataManager.save(updatedCustomer);
    saveDataManager.disable();
}

// ===============================================================================

function saveRequestToService(appservices: AppServices, updatedRequest: Models.CustomerRequest) {
    const saveDataManager = new SaveDataManager(() => toxnotServiceClient.updateCustomerRequest(updatedRequest).then(r => r.success), appservices);
    saveDataManager.initialize();
    saveDataManager.enable();
    saveDataManager.save(updatedRequest);
    saveDataManager.disable();
}

// ===============================================================================

function runDeleteSelectedCustomersProcedure(customerState: MyCustomersState, dispatch: (action: UpdateCustomersStateAction) => void) {
    const newCustomers = customerState.myCustomers.filter(c => !customerState.selectedCustomerIds[c.customerId]);

    for (let id of Object.keys(customerState.selectedCustomerIds)) {
        delete customerState.customerIdToCustomerTable[id];

        if (customerState.customerIdsToRequests !== null) {
            delete customerState.customerIdsToRequests[id];
        }

        delete customerState.selectedCustomerIds[id];
    }

    const filteredCustomers = applyCustomerFilters(newCustomers, customerState.customerIdsToRequests, customerState.activeCustomerFilters);

    dispatch({
        type: UpdateCustomersState,
        value: {
            myCustomers: newCustomers,
            filteredCustomers: filteredCustomers,
            selectedCustomerIds: { ...customerState.selectedCustomerIds },
            customerIdsToRequests: customerState.customerIdsToRequests,
            customerIdToCustomerTable: { ...customerState.customerIdToCustomerTable }
        }
    });
}

// ===============================================================================

export function getCurrentlySelectedCustomerRequestsFromState(customersState: MyCustomersState) {
    if (customersState.currentlySelectedCustomerId === null) {
        throw new Error("No customer or request has been selected.");
    }

    if (customersState.customerIdsToRequests === null) {
        throw new Error("Customer Ids to Requests Map was null");
    }

    return customersState.customerIdsToRequests[customersState.currentlySelectedCustomerId];
}


// ===============================================================================

function dispatchUpdateStateWithNewRequest(myCustomersState: MyCustomersState, newRequest: Models.CustomerRequest, dispatch: (action: UpdateCustomersStateAction) => void) {
    // If the user does not have customer requests enabled, don't bother updating the state.
    if (myCustomersState.customerIdsToRequests === null) {
        return;
    }

    const customerId = myCustomersState.currentlySelectedCustomerId;

    if (customerId === null) {
        return;
    }

    let newCustomerIdToRequestsMap: Models.CustomerIdToRequestsType = {}

    const requests = getCurrentlySelectedCustomerRequestsFromState(myCustomersState).filter(r => r.requestId !== newRequest.requestId);
    requests.unshift(newRequest);

    newCustomerIdToRequestsMap = { ...myCustomersState.customerIdsToRequests };
    newCustomerIdToRequestsMap[customerId] = requests;

    dispatch({ type: UpdateCustomersState, value: { customerIdsToRequests: newCustomerIdToRequestsMap } })
}

//
// Known Actions
//

type KnownAction = UpdateCustomersStateAction;

// ===============================================================================

function hasMyCustomersEnabled(appState: ApplicationState) {
    return appState.appServices?.userContext?.userCapabilities.myCustomersEnabled ?? false;
}

// ===============================================================================

async function onTotallyCompleteOrFailedReportRespond(
    requestId: string,
    dispatch: (action: KnownAction) => void,
    appState: ApplicationState) {

    //
    // Update responding state
    //

    const customerState = appState.myCustomersState;

    if (!customerState) {
        throw new Error("Customer State was undefined");
    }

    delete customerState.requestsResponding[requestId];

    dispatch({ type: UpdateCustomersState, value: { requestsResponding: { ...customerState.requestsResponding }, pickReportsModalResponseState: hasMyCustomersEnabled(appState) ? PickReportsModalResponseState.Default : PickReportsModalResponseState.ReportCreated } });

    //
    // Update the current request.
    //

    const requestResult = await toxnotServiceClient.customerRequestGet(requestId);

    if (!requestResult.success || requestResult.data === null) {
        appState.appServices?.displayApiErrorMessage();
        return;
    }

    dispatchUpdateStateWithNewRequest(appState.myCustomersState!, requestResult.data, dispatch);

    //
    // Update the parts for the customer.
    //

    const purchasesResult = await toxnotServiceClient.customerInventoryPurchasesGetAllForCustomer(requestResult.data.customerId);

    if (!purchasesResult.success || purchasesResult.data === null) {
        appState.appServices?.displayApiErrorMessage();
        return;
    }

    const newCustomerIdToPurchasesMap = { ...customerState.customerIdsToPurchases };

    newCustomerIdToPurchasesMap[requestResult.data.customerId] = purchasesResult.data;

    dispatch({ type: UpdateCustomersState, value: { customerIdsToPurchases: newCustomerIdToPurchasesMap } })

    //
    // Get shared material names from ids
    //

    const sharedMaterialIdsToCoreDataTableResult = await toxnotServiceClient.sharedMaterialsGetByIds(requestResult.data.requestData.passportIdsContainingReports);

    if (!sharedMaterialIdsToCoreDataTableResult.success || sharedMaterialIdsToCoreDataTableResult.data === null) {
        appState.appServices?.displayApiErrorMessage();
        return;
    }

    dispatch({ type: UpdateCustomersState, value: { sharedMaterialIdsToCoreDataTable: sharedMaterialIdsToCoreDataTableResult.data } });

    offlineProcessManager = null;
}

// ===============================================================================

function initiateOfflineProcessPolling(operationId: string, requestId: string, appState: ApplicationState, dispatch: (action: KnownAction) => void) {
    if (!appState.myCustomersState || !appState.appServices) {
        throw new Error("My Customers State was in a bad state.");
    }

    offlineProcessManager = new OfflineProcessPollingManager<BulkReportContext, BulkReportingResults>(
        operationId,
        appState.appServices!,
        (offlineProcessData: BaseOfflineProcessData<BulkReportContext, BulkReportingResults>) => {
            const customerState = verifyAndGetMyCustomerStateFromAppState(appState);

            const newRequestStatuses = { ...customerState.requestsResponding };
            newRequestStatuses[requestId] = offlineProcessData.state;

            dispatch({ type: UpdateCustomersState, value: { requestsResponding: newRequestStatuses } })
        },
        (offlineProcessData: BaseOfflineProcessData<BulkReportContext, BulkReportingResults>) => { onTotallyCompleteOrFailedReportRespond(requestId, dispatch, appState) },
        (offlineProcessData: BaseOfflineProcessData<BulkReportContext, BulkReportingResults>) => { onTotallyCompleteOrFailedReportRespond(requestId, dispatch, appState) }
    );

    offlineProcessManager.startPolling(5000);
}

// ===============================================================================

/**
 * Does all the common operations for responding to a request: updating state, responding
 * to the request, and initiating the polling that will update the user on the progress of
 * their response.
 *
 * @param requestId the request id of the request being responded to
 * @param state application state
 * @param dispatch function to update the local state
*/
async function doRespondToRequestOperations(requestId: string | null, attempToShowInfoModal: boolean, state: ApplicationState, dispatch: (action: KnownAction) => void) {
    if (attempToShowInfoModal && localStorage.getItem("dont-show-inform-about-responding-modal") !== "true") {
        $('#select-report-type-modal').modal('hide');
        $("#inform-about-responding-modal").modal("show");
        return;
    }

    if (!state.appServices) {
        throw new Error("AppServices was undefined.");
    }

    const customerState = state.myCustomersState;

    if (!customerState) {
        throw new Error("Customer State was undefined");
    }

    if (requestId === null) {
        throw new Error("Request ID was null when attempting to respond to a request");
    }

    if (customerState.requestsResponding[requestId]) {
        alert(state.appServices.localizeString("You are already responding to this request. You must wait until it completes before you can re-respond"));
        return;
    }

    customerState.requestsResponding[requestId] = { ...defaultOfflineProcessState };

    dispatch({ type: UpdateCustomersState, value: { requestsResponding: { ...customerState.requestsResponding }, pickReportsModalResponseState: PickReportsModalResponseState.CreatingReport } });

    const apiResult = await toxnotServiceClient.respondToCustomerRequests([requestId]);

    if (!apiResult.success || apiResult.data === null) {
        state.appServices.displayApiErrorMessage();
        return;
    }

    initiateOfflineProcessPolling(apiResult.data, requestId, state, dispatch);
}

// ===============================================================================

//
// Action creators
//

export const actionCreators = {
    /**
     * Load all my customer data
     */
    loadAllData: (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        verifyAndGetMyCustomerState(getState);

        await loadAllData(getState(), dispatch);
    },
    /**
     * creates a new customer, updating both the DB and local state
     */
    createNewCustomer: (callback: (customerId: string) => void): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const appState = getState();

        if (!appState || !appState.appServices) {
            return;
        }

        const customerState = verifyAndGetMyCustomerState(getState);

        const blankCutomerRequest: UpdateCustomerRequest = {
            customerId: null, // new customer
            companyName: null,
            customerContacts: [{
                name: "",
                email: ""
            }]
        };

        const apiResult = await toxnotServiceClient.updateCustomer(blankCutomerRequest);

        if (!apiResult.success || apiResult.data === null) {
            appState.appServices?.displayApiErrorMessage();
            return;
        }

        customerState.myCustomers.push(apiResult.data);

        const newCustomers = Array.from(customerState.myCustomers);

        customerState.customerIdToCustomerTable[apiResult.data.customerId] = apiResult.data;
        customerState.customerIdsToPurchases[apiResult.data.customerId] = [];

        let newCustomerIdsToRequests: {} | null = { }; 

        if (appState.appServices.userContext!.userCapabilities.myCustomersEnabled && customerState.customerIdsToRequests !== null) {
            customerState.customerIdsToRequests[apiResult.data.customerId] = [];
            newCustomerIdsToRequests = customerState.customerIdsToRequests;
        }
        else {
            newCustomerIdsToRequests = null;
        }

        const newFilteredCustomers = applyCustomerFilters(newCustomers, customerState.customerIdsToRequests, customerState.activeCustomerFilters);

        dispatch({
            type: UpdateCustomersState,
            value: {
                myCustomers: newCustomers,
                filteredCustomers: newFilteredCustomers,
                customerIdToCustomerTable: { ...customerState.customerIdToCustomerTable },
                customerIdsToRequests: newCustomerIdsToRequests,
                customerIdsToPurchases: { ...customerState.customerIdsToPurchases },
                currentlySelectedCustomerId: apiResult.data.customerId
            }
        });

        callback(apiResult.data.customerId);
    },
    /**
     * Sets the currently selected customer id
     */
    loadCustomerIntoCurrentlySelected: (customerId: string | null): AppThunkAction<KnownAction> => (dispatch, getState) => {
        verifyAndGetMyCustomerState(getState);

        dispatch({ type: UpdateCustomersState, value: { currentlySelectedCustomerId: customerId } });
    },
    /**
     * Generic action method to update the currently selected customer
     */
    updateSelectedCustomer: (updatedPartialCustomer: Partial<Models.Customer>): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const customerState = verifyAndGetMyCustomerState(getState);

        const customerId = customerState.currentlySelectedCustomerId;

        if (customerId === null) {
            throw new Error("Customer ID was null when updating customer");
        }

        const updatedCustomer: Models.Customer = {
            ...customerState.customerIdToCustomerTable[customerId],
            ...updatedPartialCustomer
        }

        const newCustomers = customerState.myCustomers.map(
            c => {
                if (c.customerId === customerId) {
                    c = updatedCustomer
                }

                return c
        });

        const newUpdateRequest: UpdateCustomerRequest = {
            customerId: customerId,
            companyName: updatedCustomer.companyName,
            customerContacts: customerState.customerIdToCustomerTable[customerId].customerContacts,
        };

        const appState = getState();

        if (!appState.appServices) {
            throw new Error("AppServices was null");
        }

        // save to service.
        saveCustomerToService(appState.appServices, newUpdateRequest);

        const updatedTable = createCustomerIdToCustomerTable(newCustomers);

        const filteredCustomers = applyCustomerFilters(newCustomers, customerState.customerIdsToRequests, customerState.activeCustomerFilters);

        // save to local state.
        dispatch({ type: UpdateCustomersState, value: { myCustomers: newCustomers, customerIdToCustomerTable: updatedTable, filteredCustomers: filteredCustomers } });
    },
    /**
     * Updates what customers are selected on the My Customers Home view
     */
    updateSelectedCustomerIds: (selectedIds: SelectedCustomerIdsType): AppThunkAction<KnownAction> => (dispatch, getState) => {
        verifyAndGetMyCustomerState(getState);

        dispatch({ type: UpdateCustomersState, value: { selectedCustomerIds: { ...selectedIds } } });
    },
    /**
     * Deletes selected customers, updating DB and local state.
     */
    deleteSelectedCustomer: (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const customerState = verifyAndGetMyCustomerState(getState);

        const apiResult = await toxnotServiceClient.deleteCustomers(Object.keys(customerState.selectedCustomerIds));

        if (!apiResult.success) {
            const appState = getState();
            appState.appServices?.displayApiErrorMessage();
            return;
        }

        runDeleteSelectedCustomersProcedure(customerState, dispatch);
    },
    /**
     * adds a filter for the My Customers Home View
     */
    addFilter: (filter: Filter): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const customerState = verifyAndGetMyCustomerState(getState);

        const newFilters = removeFilter(filter, customerState.activeCustomerFilters);
        newFilters.push(filter);

        const newFilteredCustomers = applyCustomerFilters(customerState.myCustomers, customerState.customerIdsToRequests, newFilters);

        dispatch({ type: UpdateCustomersState, value: { activeCustomerFilters: newFilters, filteredCustomers: newFilteredCustomers } });
    },
    /**
     * removes a given filter
     */
    removeCustomerFilter: (filter: Filter): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const customerState = verifyAndGetMyCustomerState(getState);

        const newFilters = removeFilter(filter, customerState.activeCustomerFilters);

        const newFilteredCustomers = applyCustomerFilters(customerState.myCustomers, customerState.customerIdsToRequests, newFilters);

        dispatch({ type: UpdateCustomersState, value: { activeCustomerFilters: newFilters, filteredCustomers: newFilteredCustomers } });
    },
    /**
     * removes any filters of id CustomerFilterId.HasActiveRequests
     */
    clearActiveRequestsFilters: (): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const customerState = verifyAndGetMyCustomerState(getState);

        const newFilters = customerState.activeCustomerFilters.filter(f => f.filterId !== FilterId.HasActiveRequests);

        const newFilteredCustomers = applyCustomerFilters(customerState.myCustomers, customerState.customerIdsToRequests, newFilters);

        dispatch({ type: UpdateCustomersState, value: { activeCustomerFilters: newFilters, filteredCustomers: newFilteredCustomers } });
    },
    /**
     * Uploads a spreedsheet of customers to the DB and updates local state
     */
    uploadCustomers: (file: File): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        verifyAndGetMyCustomerState(getState);

        const apiResult = await toxnotServiceClient.uploadCustomers(file);

        const state = getState();

        if (apiResult.result === ResultType.UploadTooLarge) {
            state.appServices?.displayApiErrorMessage("Sorry, your current account does not let you upload files of that size.");
        }

        if (!apiResult.success) {
            state.appServices?.displayApiErrorMessage();
            return;
        }
    },
    /**
     * Updates the request notes for a request
     */
    updateRequestNotes: (newNotes: string, request: Models.CustomerRequest): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const myCustomersState = verifyAndGetMyCustomerState(getState);

        const state = getState();

        if (!state.appServices) {
            return;
        }

        const newRequest: Models.CustomerRequest = {
            ...request,
            requestData: {
                ...request.requestData,
                notes: newNotes
            }
        }

        // save to service
        saveRequestToService(state.appServices, newRequest);
       
        // save to local state
        dispatchUpdateStateWithNewRequest(myCustomersState, newRequest, dispatch);
    },
    /**
     * Starts the process of responding to a customer request.
     */
    respondToRequest: (requestId: string | null, attempToShowInfoModal: boolean): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        doRespondToRequestOperations(requestId, attempToShowInfoModal, getState(), dispatch);
    },
    /**
     * Generic action method to update currently selected request
     */
    updateCurrentlySelectedRequest: (updatedRequest: Partial<Models.CustomerRequest>, requestId: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const state = getState();

        const customerState = verifyAndGetMyCustomerState(getState);

        const existingRequest = getRequestFromState(customerState, requestId);

        const newRequest: Models.CustomerRequest = {
            ...existingRequest,
            ...updatedRequest,
            requestData: {
                ...existingRequest.requestData,
                ...updatedRequest.requestData
            }
        }

        if (!state.appServices) {
            return;
        }

        // save to service
        saveRequestToService(state.appServices, newRequest);

        // save to local state
        dispatchUpdateStateWithNewRequest(customerState, newRequest, dispatch);
    },
    /**
     * Updates the active request from the database instead of memory.
     */
    updateRequestFromService: (requestId: string): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const customerState = verifyAndGetMyCustomerState(getState);

        const apiResult = await toxnotServiceClient.customerRequestGet(requestId);

        if (!apiResult.success || apiResult.data === null) {
            const appState = getState();
            appState.appServices?.displayApiErrorMessage();
            return;
        }

        dispatchUpdateStateWithNewRequest(customerState, apiResult.data, dispatch);
    },
    /**
     * Adds a purchases for a customer
     */
    addCustomerPurchases: (inventoryIds: string[]): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const customerState = verifyAndGetMyCustomerState(getState);

        const customerId = customerState.currentlySelectedCustomerId;

        if (customerId === null) {
            throw new Error("Customer Id was null");
        }

        const apiResult = await toxnotServiceClient.customerInventoryPurchasesUpdate(customerId, inventoryIds);

        if (!apiResult.success || apiResult.data === null) {
            const appState = getState();
            appState.appServices?.displayApiErrorMessage();
            return;
        }

        const newCustomerIdToPurchasesMap = { ...customerState.customerIdsToPurchases };

        newCustomerIdToPurchasesMap[customerId] = apiResult.data;

        dispatch({ type: UpdateCustomersState, value: { customerIdsToPurchases: newCustomerIdToPurchasesMap } })
    },
    /**
     * Removes purchases for a customer
     */
    deletePurchases: (inventoryIds: string[]): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const customerState = verifyAndGetMyCustomerState(getState);

        const customerId = customerState.currentlySelectedCustomerId;

        if (customerId === null) {
            throw new Error("Customer Id was null");
        }

        const apiResult = await toxnotServiceClient.customerInventoryPurchasesDelete(customerId, inventoryIds);

        if (!apiResult.success) {
            const appState = getState();
            appState.appServices?.displayApiErrorMessage();
            return;
        }

        const newCustomerIdToPurchasesMap = { ...customerState.customerIdsToPurchases };

        newCustomerIdToPurchasesMap[customerId] = newCustomerIdToPurchasesMap[customerId].filter(p => !inventoryIds.includes(p.simpleInventory.inventoryId));

        dispatch({ type: UpdateCustomersState, value: { customerIdsToPurchases: newCustomerIdToPurchasesMap } })
    },
    /**
     * Sets the currently selected request so the list modal can access
     */
    setRequestIdForModal: (requestId: string | null): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        verifyAndGetMyCustomerState(getState);

        dispatch({ type: UpdateCustomersState, value: { respondingRequestIdForModals: requestId } })
    },
    /**
     * Removes an inventory to report from the local state
     */
    removeInventoryToReport: (inventoryIndex: number, requestId: string): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const customerState = verifyAndGetMyCustomerState(getState);

        const request = getRequestFromState(customerState, requestId);

        const newInventoriesToReport = request.requestData.inventoriesToReport.filter((inv, index) => index !== inventoryIndex);

        const newRequest: Models.CustomerRequest = {
            ...request,
            requestData: {
                ...request.requestData,
                inventoriesToReport: newInventoriesToReport
            }
        }

        const state = getState();

        if (!state.appServices) {
            return;
        }

        // save to service
        saveRequestToService(state.appServices, newRequest);

        // save to local state
        dispatchUpdateStateWithNewRequest(customerState, newRequest, dispatch);
    },
    /**
     * Adds inventories to a request
     */
    addInventoriesToReport: (requestId: string, inventoryIds: string[]): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const customerState = verifyAndGetMyCustomerState(getState);

        const state = getState();

        if (!state.appServices) {
            return;
        }

        const apiResult = await toxnotServiceClient.customerRequestUpdateInventoriesToReport(requestId, inventoryIds);

        if (!apiResult.success || apiResult.data === null) {
            state.appServices?.displayApiErrorMessage();
            return;
        }

        // save to local state
        dispatchUpdateStateWithNewRequest(customerState, apiResult.data, dispatch);
    },
    /**
     * Updates the local state with shared material core data for a list of shared material ids.
     */
    getSharedMaterialsFromIds: (sharedMaterialIds: string[]): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        verifyAndGetMyCustomerState(getState);

        const apiResult = await toxnotServiceClient.sharedMaterialsGetByIds(sharedMaterialIds);

        if (!apiResult.success || apiResult.data === null) {
            const appState = getState();
            appState.appServices?.displayApiErrorMessage();
            return;
        }

        dispatch({ type: UpdateCustomersState, value: { sharedMaterialIdsToCoreDataTable: apiResult.data } })
    },
    /**
     * Set local storage so the responding info request modal is shown or never shown again
     */
    setDontShowInfoModal: (val: boolean): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const stringVal = val ? "true" : "false";
        localStorage.setItem("dont-show-inform-about-responding-modal", stringVal);
    },
    /**
     * Sets a request to create in the local state. This is primarily used to save a request across different components
     */
    setRequestForCreateRequest: (request: CreateReportRequest | null): AppThunkAction<KnownAction> => (dispatch, getState) => {
        verifyAndGetMyCustomerState(getState);

        dispatch({ type: UpdateCustomersState, value: { requestForCreateReportRequest: request } });
    },
    /**
     * Creates a customer request and responds to it. This would be for a pro-active share
     */
    doCreateRequestForCurrentCustomerOperation: (attempToShowInfoModal: boolean, history?: H.History): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const customerState = verifyAndGetMyCustomerState(getState);

        const requestToCreate = customerState.requestForCreateReportRequest;

        if (requestToCreate === null) {
            throw new Error("The request to create was null");
        }

        const appServices = getState().appServices;

        if (!appServices) {
            return;
        }

        const apiResultForRequest = await toxnotServiceClient.createCustomerReportRequest(requestToCreate);

        if (!apiResultForRequest.success || apiResultForRequest.data === null) {
            appServices.displayApiErrorMessage();
            return;
        }

        dispatchUpdateStateWithNewRequest(customerState, apiResultForRequest.data, dispatch);
        dispatch({ type: UpdateCustomersState, value: { respondingRequestIdForModals: apiResultForRequest.data.requestId } });

        if (ReportTypesManager.doesCustomerRequestRequireList(apiResultForRequest.data)) {
            $('#select-report-type-modal').modal('hide');
            $('#select-list-modal').modal('show');
            return;
        }

        if (history) {
            $('#select-report-type-modal').modal('hide');
            history.push(`${ToxnotReactComponentPaths.MyCustomerDetailBasePath}/${customerState.currentlySelectedCustomerId}/Request/${apiResultForRequest.data.requestId}`)
        }

        doRespondToRequestOperations(apiResultForRequest.data.requestId, attempToShowInfoModal, getState(), dispatch);
    },
    /**
     * Sets the pick reports modal response State
     */
    setPickReportsModalResponseState: (state: PickReportsModalResponseState): AppThunkAction<KnownAction> => (dispatch, getState) => {
        verifyAndGetMyCustomerState(getState);

        dispatch({ type: UpdateCustomersState, value: { pickReportsModalResponseState: state } });
    }
};

// ===============================================================================

//
// Reducer
//

export const reducer: Reducer<MyCustomersState> = (state: MyCustomersState | undefined, action: Action): MyCustomersState => {
    if (state === undefined) {
        return initialState
    }

    switch (action.type) {
        case UpdateCustomersState:
            return {
                ...state,
                ...(action as UpdateCustomersStateAction).value
            };
    }

    return state;
}
